import { api } from './base';

export interface AppPassword {
  id: number;
  employee_id: number;
  remark: string;
  expires_at?: string;
  create_time: string;
}
export interface EmailSummery {
  id: number;
  from_email: string;
  message_id: string;
  only_inner_domain: boolean;
  send_at: string;
  subject: string;
  undo_committed: boolean;
}

export interface MailingListRecord {
  id: number;
  name: string;
  description: string;
  members: string[];
  emails: string[];
}

export interface MailingListModifyRequest {
  action: 'addItem' | 'removeItem';
  members: string[];
}

export interface EmailArchiveItem {
  id: number;
  message_id: string;
  from_email: string;
  to_email_list: string[];
  subject: string;
  send_at: string | null;
  create_time: string | null;
  eml_file_path: string;
}

export interface QueryEmailsParams {
  from_email?: string;
  to_email?: string;
  send_at_start?: string;
  send_at_end?: string;
  keyword?: string;
  message_id?: string;
  skip: number;
  limit: number;
}

export interface QueryEmailsResult {
  items: EmailArchiveItem[];
  total: number;
  skip: number;
  limit: number;
}

export interface EmailBodyResult {
  message_id: string;
  subject: string;
  from_email: string;
  to_email_list: string[];
  send_at: string | null;
  body_text: string;
}

export interface EmlAttachment {
  filename: string;
  content_type: string;
  content_id: string | null;
  disposition: string;
  data: string;
}

export interface MailParts {
  from_addr: string;
  title: string;
  content: string;
  sent_time: string;
  recipients: string[];
  cc_recipients: string[];
  bcc_recipients: string[];
  attachments: EmlAttachment[];
  reply_to_message_id: string | null;
  message_id: string | null;
  references: string | null;
}

export interface ParsedEml extends MailParts {
  message_id: string | null;
  in_reply_to: string | null;
}

export interface ImportRequest {
  mail_parts: MailParts;
  import_emails: string[];
}

export interface ImportResult {
  imported: string[];
  failed: string[];
}

export const emailApis = {
  listAppPasswords: (): Promise<{ items: AppPassword[] }> => api.get('/mail/app-password'),
  createAppPassword: (remark: string, expires_in_days: number, plain_password: string): Promise<void> => {
    return api.post('/mail/app-password', {
      remark,
      expires_in_days,
      plain_password
    });
  },
  deleteAppPassword: (app_password_id: number): Promise<void> => api.delete(`/mail/app-password/${app_password_id}`),
  listAllowUndoEmails: (): Promise<{ items: EmailSummery[]; total: number; tips: string }> => {
    return api.get('/mail/undo-documents');
  },
  undoEmail: (emailId: number): Promise<{ success: boolean; message: string }> => {
    return api.post(`/mail/undo-documents/${emailId}/undo`);
  },
  mailingListsList(params: {
    query: string;
    page: number;
    page_size: number;
  }): Promise<{ items: MailingListRecord[]; total: number }> {
    const queryString = new URLSearchParams(params as any).toString();
    return api.get(`/mail/mailing-lists?${queryString}`);
  },
  mailingListsModify(list_name: string, data: MailingListModifyRequest): Promise<void> {
    return api.post(`/mail/mailing-lists/${encodeURIComponent(list_name)}/members`, data);
  },
  mailingListsSyncToDb(): Promise<void> {
    return api.post('/mail/mailing-lists/sync-to-db');
  },
  mailingListsCleanResignedMembers(): Promise<void> {
    return api.post('/mail/mailing-lists/clean-resigned-members');
  },
  mailingListsImportValidate({
    numbers,
    usernames
  }: {
    numbers?: string[];
    usernames?: string[];
    emails?: string[];
  }): Promise<{ items: string[] }> {
    return api.post(`/mail/mailing-lists/import-validate`, {
      numbers,
      usernames
    });
  },
  queryEmails(params: QueryEmailsParams): Promise<QueryEmailsResult> {
    const queryParts: string[] = [];
    if (params.from_email) queryParts.push(`from_email=${encodeURIComponent(params.from_email)}`);
    if (params.to_email) queryParts.push(`to_email=${encodeURIComponent(params.to_email)}`);
    if (params.send_at_start) queryParts.push(`send_at_start=${encodeURIComponent(params.send_at_start)}`);
    if (params.send_at_end) queryParts.push(`send_at_end=${encodeURIComponent(params.send_at_end)}`);
    if (params.keyword) queryParts.push(`keyword=${encodeURIComponent(params.keyword)}`);
    if (params.message_id) queryParts.push(`message_id=${encodeURIComponent(params.message_id)}`);
    queryParts.push(`skip=${params.skip}`);
    queryParts.push(`limit=${params.limit}`);
    return api.get(`/mail/emails?${queryParts.join('&')}`);
  },
  getEmailBody(messageId: string): Promise<EmailBodyResult> {
    return api.get(`/mail/emails/body?message_id=${encodeURIComponent(messageId)}`);
  },
  getEmlDownloadUrl(messageId: string): string {
    return `/api/mail/emails/download?message_id=${encodeURIComponent(messageId)}`;
  },
  importMails(data: ImportRequest): Promise<ImportResult> {
    return api.post('/mail/import', data);
  },
  parseImportEml(file: File): Promise<ParsedEml> {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/mail/import/parse', formData);
  }
};
